import React from 'react';

import Label from 'shared/components/label';
import Title from 'src/components/AccountSettings/Title';
import 'stylesheets/components/account-settings-form-container.scss';
import {Formik} from 'formik';
import AddressVerificationModal from 'src/components/address-verification-modal/address-verification-modal';
import FormikAddressInput from 'src/components/inputs/formik-address-input/formik-address-input';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import SexInput from 'src/components/inputs/sex-input/sex-input';
import CitizenshipInput from 'src/components/inputs/citizenship-input/citizenship-input';
import RaceOrEthnicity from 'src/components/race-ethnicity/race-ethnicity';
import FirstGenInput from 'src/components/inputs/first-gen-input/first-gen-input';
import LowIncomeInput from 'src/components/inputs/low-income-input/low-income-input';
import MilitaryServiceInput from 'src/components/inputs/military-service-input/military-service-input';
import AssociatesDegreeInput from 'src/components/inputs/associates-degree-input/associates-degree-input';
import ReligionInput from 'src/components/inputs/religion-input';
import GenderInput from 'src/components/inputs/gender-input';
import InstitutionTypeahead from 'shared/institution-typeahead/components/institution-typeahead';
import {CommunityCollegeInfo, CurrentInstitution} from 'src/types/store';
import BirthdayInput from 'src/components/inputs/birthday-input/birthday-input';
import {
  profileFormSchemaCreator,
  ProfileFormSchemaFields,
} from 'src/components/AccountSettings/profile-form/schema';
import ProfileFormFooter from 'src/components/AccountSettings/profile-form/profile-form-footer/profile-form-footer';
import {
  Ethnicity,
  Gender,
  IsUsCitizenOrResident,
  Sex,
} from 'src/components/AccountSettings/profile-form/utils';
import {useDispatch} from 'react-redux';
import {errorMessage} from 'shared/actions/notifications';

interface ProfileFormFields {
  _id: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  subdivision: string;
  country: string;
  postalCode: string;
  birthdayDay: number;
  birthdayMonth: number;
  birthdayYear: number;
  sex: Sex;
  gender: Gender;
  customGenderIdentity?: string;
  ethnicity: Ethnicity;
  religion: string;
  customReligion?: string;
  isUsCitizenOrResident?: IsUsCitizenOrResident;
  isFirstGen: boolean;
  currentInstitution: CurrentInstitution;
  communityCollegeInfo: CommunityCollegeInfo;
}

const TRANSFER_PROFILE_FORM_VALIDATION_SCHEMA: ProfileFormSchemaFields = [
  'firstName',
  'lastName',
  'addressLine1',
  'city',
  'subdivision',
  'country',
  'postalCode',
  'birthday',
  'gender',
  'ethnicity',
  'religion',
  'usCitizenOrResident',
  'lunchPlan',
  'isFirstGen',
];

interface Props {
  onSubmit: (fields: ProfileFormFields) => void;
  initialValues: ProfileFormFields;
}

const ProfileForm = ({onSubmit, initialValues}: Props) => {
  const dispatch = useDispatch();

  return (
    <div data-testid="profile-form">
      <Formik<ProfileFormFields>
        initialValues={initialValues}
        validationSchema={profileFormSchemaCreator(TRANSFER_PROFILE_FORM_VALIDATION_SCHEMA, {})}
        onSubmit={onSubmit}
      >
        {({validateForm}) => (
          <div className="account-settings-form">
            <AddressVerificationModal />
            <Title>Edit Profile</Title>
            <form autoComplete="off">
              <legend className="shared-required-legend"></legend>
              <section className="account-settings-form-section related">
                <div className="form-container">
                  <div className="account-settings-form-section-side-by-side">
                    <Label required>
                      First Name
                      <FormikTextInput
                        required
                        name="firstName"
                        placeholder="e.g. Johnnetta"
                        showCheck
                      />
                    </Label>
                    <Label required>
                      Last Name
                      <FormikTextInput required name="lastName" placeholder="e.g. Cole" showCheck />
                    </Label>
                  </div>
                </div>
              </section>
              <section className="account-settings-form-section related">
                <div className="form-container address-input">
                  <InstitutionTypeahead
                    institutionType="community-college"
                    renderLabel={() => 'Current College'}
                    showCampus
                  />
                </div>
              </section>
              <section className="account-settings-form-section related">
                <div className="form-container address-input">
                  <FormikAddressInput />
                </div>
              </section>
              <section className="account-settings-form-section">
                <div className="form-container">
                  <BirthdayInput />
                </div>
              </section>
              <section className="account-settings-form-section">
                <GenderInput />
              </section>

              <section className="account-settings-form-section">
                <SexInput />
              </section>
              <section className="account-settings-form-section">
                <CitizenshipInput />
              </section>
              <section className="account-settings-form-section">
                <RaceOrEthnicity />
              </section>

              <section className="account-settings-form-section">
                <FirstGenInput />
              </section>
              <section className="account-settings-form-section">
                <LowIncomeInput />
              </section>
              <section className="account-settings-form-section">
                <MilitaryServiceInput />
              </section>
              <section className="account-settings-form-section">
                <AssociatesDegreeInput />
              </section>
              <section className="account-settings-form-section">
                <div className="form-container">
                  <ReligionInput />
                </div>
              </section>
            </form>
            <ProfileFormFooter<ProfileFormFields>
              onSubmitClick={async () => {
                const errors = await validateForm();
                if (Object.keys(errors).length > 0) {
                  dispatch(errorMessage(`Form errors: ${Object.values(errors).join(', ')}`));
                }
              }}
            />
          </div>
        )}
      </Formik>
    </div>
  );
};

export default ProfileForm;
